import React from "react";
import moment from "moment";
import styled from "styled-components";
import {Spin} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import {courierStatuses, courierStatusColors} from "constants/constants";
import {
    PopupContainer,
    Header,
    TitleContainer,
    Icon,
    Title,
    Section,
    SectionTitle,
    List,
    ListItem,
    Status,
    NoData
} from "components/customer/customerHomePageWithShiftScore/style/CourierPopup.style";
import {CourierIcon} from "components/_defaultComponents/CourierIcon/CourierIcon";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";

const deliveryStateLabels = {
    unassigned: "Kurye Atandı",
    waiting: "Kurye Atandı",
    onTheWayForPickUp: "Taşıma Aşamasında",
    onPickUpAddress: "Taşıma Aşamasında",
    onDelivery: "Taşıma Aşamasında",
    onDeliveryAddress: "Taşıma Aşamasında",
    delivered: "Teslim Edildi"
};

const getStatusName = status => courierStatuses.find(s => s.value === status)?.name || status;

const PopupTitleLink = styled.a`
    color: inherit;

    &:hover {
        color: #1890ff;
    }
`;

// Bölüm başlığı ile sağdaki detay linkini aynı satırda tutar
const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
`;

const SectionHeading = styled(SectionTitle)`
    margin-bottom: 0;
`;

const DetailLink = styled.a`
    display: flex;
    align-items: center;
    color: #8c8c8c;
    font-size: 16px;

    &:hover {
        color: #1890ff;
    }
`;

const SectionDivider = styled.div`
    border-top: 1px solid #f0f0f0;
    margin: 10px 0;
`;

const OrderNumber = styled.span`
    font-weight: 600;
`;

export const MapCourierPopup = ({courier, courierInfo, courierInfoLoading}) => {
    const phone = courier?.courier?.cellPhone;
    const status = courier?.status;
    const delivery = courier?.delivery;

    const poolName = courier?.dedicatedPoolNames?.length
        ? courier.dedicatedPoolNames.join(", ")
        : courier?.poolName || courierInfo?.poolShift?.name;
    const shift = courierInfo?.poolShift || courierInfo?.dedicatedShift;
    const courierId = courier?.courier?._id;
    const deliveryId = delivery?._id;
    const orderNumber = delivery?.orders?.[0]?.orderNumber;
    const pickup = delivery?.pickupInfos?.[0];
    // maplist yanıtında pickupInfos yalnızca _id ve name taşıyor; firma adı delivery.company'de
    const companyName = delivery?.company?.name || pickup?.companyName;

    return (
        <PopupContainer>
            <Header>
                <TitleContainer>
                    <Icon>
                        <CourierIcon courierType={courier?.type || "motorCourier"} />
                    </Icon>
                    <Title>
                        {courierId ? (
                            <PopupTitleLink
                                href={`/couriers/${courierId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {courier?.courier?.fullname || "Kurye"}
                            </PopupTitleLink>
                        ) : (
                            courier?.courier?.fullname || "Kurye"
                        )}
                    </Title>
                </TitleContainer>
            </Header>

            <Section>
                <SectionHeader>
                    <SectionHeading>Kurye Bilgileri</SectionHeading>
                    {courierId ? (
                        <DetailLink
                            href={`/couriers/${courierId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Kurye Detayı"
                        >
                            <InfoCircleOutlined />
                        </DetailLink>
                    ) : null}
                </SectionHeader>
                <List>
                    {phone && <ListItem>Telefon: {phone}</ListItem>}
                    <ListItem>
                        Durum:{" "}
                        <Status style={{color: courierStatusColors[status] || "#d9d9d9"}}>
                            {getStatusName(status)}
                        </Status>
                    </ListItem>
                    {poolName && (
                        <ListItem>
                            Havuz: <Shortener text={poolName} length={28} zIndex={10001} />
                        </ListItem>
                    )}
                    <ListItem>
                        Son Konum Güncelleme:{" "}
                        {courier?.serverTime
                            ? moment(courier.serverTime).format("DD.MM.YYYY / HH:mm")
                            : "-"}
                    </ListItem>
                    <ListItem>
                        Vardiya:{" "}
                        {courierInfoLoading ? (
                            <Spin size="small" />
                        ) : shift?.shiftStart ? (
                            `${moment(shift.shiftStart).format("HH:mm")} - ${moment(
                                shift.shiftEnd
                            ).format("HH:mm")}`
                        ) : (
                            "-"
                        )}
                    </ListItem>
                </List>
            </Section>

            <SectionDivider />

            <Section>
                <SectionHeader>
                    <SectionHeading>Sipariş Bilgileri</SectionHeading>
                    {deliveryId ? (
                        <DetailLink
                            href={`/orders/${deliveryId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Sipariş Detayı"
                        >
                            <InfoCircleOutlined />
                        </DetailLink>
                    ) : null}
                </SectionHeader>
                {delivery ? (
                    <List>
                        <ListItem>
                            Sipariş No:{" "}
                            {deliveryId && orderNumber ? (
                                <PopupTitleLink
                                    href={`/orders/${deliveryId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <OrderNumber>{orderNumber}</OrderNumber>
                                </PopupTitleLink>
                            ) : (
                                <OrderNumber>{orderNumber || "-"}</OrderNumber>
                            )}
                        </ListItem>
                        <ListItem>
                            Alt Firma:{" "}
                            {pickup?._id && pickup?.name ? (
                                <PopupTitleLink
                                    href={`/companies/sub-company/${pickup._id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {pickup.name}
                                </PopupTitleLink>
                            ) : (
                                pickup?.name || "-"
                            )}
                        </ListItem>
                        {companyName ? <ListItem>Firma: {companyName}</ListItem> : null}
                        <ListItem>
                            Durum:{" "}
                            {deliveryStateLabels[delivery.deliveryState] || delivery.deliveryState}
                        </ListItem>
                    </List>
                ) : (
                    <NoData>Bu kurye için sipariş bulunamadı.</NoData>
                )}
            </Section>
        </PopupContainer>
    );
};
